/**
 * Which board a leaderboard shows lives in the address, one set of keys per
 * board — `level.<id>`, `division.<id>` and `cursor.<id>`, the id a part's or
 * OVERALL. They were per board while every board shared the event's page; a
 * board's own screen (lib/event-boards.ts) reads the same keys, so the code
 * that writes them did not change.
 *
 * Before each part carried its own board, the page showed one, named by
 * `?part=&level=&division=&cursor=`. Inbox lines still link that way, and an
 * installed page may too, so those keys are read as the named board's own,
 * and folded into its keys the first time a tab is chosen.
 */

/**
 * A board's page size. Ten while every board shared the event's page; since
 * each has a screen of its own (10 October), a screen's worth.
 */
export const BOARD_PAGE = 25;

/** What the address says about one board; null where it says nothing. */
export type NamedBoard = { level: string | null; division: string | null; cursor: string | null };

const KEYS = ["level", "division", "cursor"] as const;

export function namedBoard(search: URLSearchParams, id: string): NamedBoard {
  const legacy = search.get("part") === id;
  const read = (key: (typeof KEYS)[number]) => search.get(`${key}.${id}`) ?? (legacy ? search.get(key) : null);
  return { level: read("level"), division: read("division"), cursor: read("cursor") };
}

/**
 * The address with one board's choice changed and every other board's kept.
 * A cursor left out goes back to the board's first page.
 */
export function boardAddress(
  search: URLSearchParams,
  id: string,
  board: { level: string | null; division: string; cursor?: string | null },
): string {
  const next = new URLSearchParams(search);
  const legacy = next.get("part");
  if (legacy) {
    for (const key of KEYS) {
      const value = next.get(key);
      if (value !== null && !next.has(`${key}.${legacy}`)) next.set(`${key}.${legacy}`, value);
      next.delete(key);
    }
    next.delete("part");
  }
  const put = (key: string, value: string | null | undefined) => {
    if (value) next.set(`${key}.${id}`, value);
    else next.delete(`${key}.${id}`);
  };
  put("level", board.level);
  put("division", board.division);
  put("cursor", board.cursor);
  return `?${next}`;
}
